import {
  mayRetain,
  noConsent,
  type ConsentIntent,
  type VerificationMethod,
} from '../../auth/consent/consentModel'

/**
 * What the parent's tap on *"Yes, keep her progress"* MEANS, expressed in the
 * vocabulary that already exists.
 *
 * 🔴 `consentModel.ts` IS NOT RE-INVENTED HERE, AND THAT IS THE POINT.
 * `ConsentIntent`, `VerificationMethod` and `mayRetain` were written in §7 for
 * the A5 gate, which deliberately writes **nothing** — OD-50: *"a tick on a
 * shared classroom laptop proves nothing about who ticked it"*, so
 * `isVerifiable` returns false and `mayRetain` returns false, and the row is
 * never created.
 *
 * §9.4 is the other side of that decision: the parent is on **their own phone**,
 * having just proved control of the number the school recorded against their
 * child's roll number. That is the one method the model names — `'phone-otp'` —
 * and it is what flips `mayRetain` to true. Writing a second consent vocabulary
 * here would leave two definitions of "may we keep this", disagreeing the first
 * time one of them changed.
 *
 * ⚠️ THIS IS A CLIENT-SIDE ASSERTION, NOT THE CONTROL. The server writes
 * `verificationMethod: 'phone-otp'` itself and never accepts one from the
 * request — *"a caller-supplied verification method is a caller asserting that
 * verification happened."* This function exists so the screen's claim is
 * explicit and testable, not so the browser can authorise anything.
 */

/** The only method §9.4 can honestly claim. `'school-attested'` has no flow. */
export const PARENT_VERIFICATION: VerificationMethod = 'phone-otp'

/**
 * The intent a parent expresses by granting on C5's consent screen.
 *
 * ⚠️ `camera` AND `whatsapp` ARE UNTOUCHED, because ADR-016 keeps the three
 * consents separate: *"collapsing them means either revoking messaging silently
 * destroys a child's learning history, or — worse — data is retained on the
 * strength of a marketing opt-in."* The camera choice belongs to the device
 * (A5) and messaging belongs to `whatsappOptIn` in C5's account half. This
 * screen decides exactly one of the three.
 */
export function retentionIntent(at: number): ConsentIntent {
  return {
    choices: { ...noConsent(), retain: true },
    verifiedBy: PARENT_VERIFICATION,
    at,
  }
}

/**
 * Whether this screen may ask the server to write a `ParentalConsent` row.
 *
 * 🔴 A GUARD AGAINST THE FALSE GREEN, ONE LAYER OUT. §4.3 reads that row as the
 * legal floor, so a row written without verification would *turn retention on
 * for a child whose parent may never have been asked* while making launch
 * blocker B2 look satisfied. A false green is more dangerous than a red.
 */
export const mayWriteConsent = (intent: ConsentIntent): boolean => mayRetain(intent)
